// src/lib/locations/getLocations.ts
import type { OrgLocation } from "@/config/locations";
import { LOCATIONS } from "@/config/locations";
import { VIRTUAL_LOCATIONS } from "@/config/virtualLocations";
import { supabase } from "@/lib/supabase";

type DbLocRow = {
  id?: string;
  name?: string;
  city?: string;
  state?: string;
  phone?: string;
  address_1?: string;
  postal_code?: string;
};

function slugPart(s: string) {
  return s.trim().toLowerCase().replace(/[^a-z0-9]+/g, "_").replace(/_+/g, "_").replace(/^_+|_+$/g, "");
}

/**
 * Canonical ID for physical lots.
 * If you *must* keep legacy ids, change this to match your existing catalog.
 */
function physicalLotId(city: string, state: string) {
  const c = slugPart(city || "unknown");
  const st = slugPart(state || "na");
  return `lot_${c}_${st}`;
}

function toFullName(city: string, state: string) {
  const c = (city || "").trim();
  const s = (state || "").trim();
  return s ? `${c}, ${s}` : c;
}

function normalizeAddress(address: string, city: string, state: string, zip: string) {
  const a = (address || "").trim();
  const c = (city || "").trim();
  const s = (state || "").trim();
  const z = (zip || "").trim();

  if (!a) return "";
  if (c && s) return `${a}, ${c}, ${s}${z ? ` ${z}` : ""}`;
  return a;
}

export async function fetchDynamicLocations(opts?: { force?: boolean }): Promise<OrgLocation[]> {
  void opts; // API shape preserved; no special force behavior for DB reads.
  if (!supabase) throw new Error("Supabase client unavailable");
  const { data, error } = await supabase
    .from("locations")
    .select("id, name, city, state, phone, address_1, postal_code, is_active")
    .eq("is_active", true)
    .order("name", { ascending: true });

  if (error) throw new Error(error.message);
  const rows: DbLocRow[] = Array.isArray(data) ? data : [];

  return rows
    .map((x) => {
      const city = (x.city || x.name || "").trim();
      const state = (x.state || "").trim();

      const id =
        typeof x.id === "string" && x.id.trim()
          ? x.id.trim()
          : physicalLotId(city || x.name || "unknown", state || "na");

      const loc: OrgLocation = {
        id,
        city: city || (x.name || "").trim(),
        state,
        fullName: toFullName(city || (x.name || "").trim(), state) || (x.name || "").trim(),
        address: normalizeAddress(x.address_1 || "", city || (x.name || "").trim(), state, x.postal_code || ""),
        phone: (x.phone || "").trim() || undefined,
        aliases: [],
      };

      return loc;
    })
    .filter((l) => l.id && (l.city || l.fullName));
}

/** Static catalog for legacy lot_* assignments, plus every org DB location as its own id. */
export function mergeOrgLocationCatalog(
  staticLots: OrgLocation[],
  dynamic: OrgLocation[],
  virtual: OrgLocation[]
): OrgLocation[] {
  const byId = new Map<string, OrgLocation>();
  for (const loc of staticLots) byId.set(loc.id, loc);
  for (const dyn of dynamic) {
    if (!dyn.id) continue;
    const existing = byId.get(dyn.id);
    if (existing) {
      byId.set(dyn.id, {
        ...existing,
        city: dyn.city || existing.city,
        state: dyn.state || existing.state,
        fullName: dyn.fullName || existing.fullName,
        address: dyn.address || existing.address,
        phone: dyn.phone || existing.phone,
        aliases: existing.aliases,
      });
      continue;
    }
    byId.set(dyn.id, dyn);
  }
  for (const loc of virtual) byId.set(loc.id, loc);
  return Array.from(byId.values());
}

export async function getLocationsMerged(opts?: { force?: boolean }): Promise<OrgLocation[]> {
  let dynamic: OrgLocation[] = [];

  try {
    dynamic = await fetchDynamicLocations({ force: !!opts?.force });
  } catch (e) {
    console.warn("[locations] dynamic fetch failed; falling back to static", e);
  }

  return mergeOrgLocationCatalog(LOCATIONS, dynamic, VIRTUAL_LOCATIONS);
}